import { useCallback, useMemo } from "react";
import { flatten, noop, uniqBy } from "lodash";
import type { QueryKey } from "react-query";
import { useInfiniteQuery } from "react-query";
import type { ChatRoomParticipant } from "@/react/types/Chat/ChatRoomParticipant";
import { reactQueryGet } from "@circle-react/helpers/backendRequestHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import { queryClient } from "@circle-react/providers/QueryClient/reactQueryConfig";

export const PARTICIPANTS_DEFAULT_PER_PAGE = 100;

export const useChatParticipantsApi = (
  chatRoomUuid?: string,
  params = {
    shouldDisableCache: false,
    per_page: PARTICIPANTS_DEFAULT_PER_PAGE,
  },
) => {
  const queryKey: QueryKey = ["chat-participants", chatRoomUuid];
  const { shouldDisableCache, ...apiParams } = params;

  const fetchParticipants = (page: number) =>
    reactQueryGet(
      internalApi.chatRoomParticipants.index({
        uuid: chatRoomUuid,
        params: { page, ...apiParams },
      }),
    );

  const getCachedParticipants = useCallback(() => {
    const data = queryClient.getQueryData<{
      pages?: Array<{ records: Array<ChatRoomParticipant> }>;
    }>(queryKey);
    const participants = flatten((data?.pages || []).map(page => page.records));
    return uniqBy(participants, "id");
  }, [queryKey]);

  const {
    data,
    isLoading,
    refetch: refetchChatParticipants,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery(
    queryKey,
    ({ pageParam = 1 }) => fetchParticipants(pageParam),
    {
      select: data => ({
        pages: data.pages,
        pageParams: data.pageParams,
        participants: uniqBy(
          flatten((data.pages || []).map(page => page.records)),
          "id",
        ),
        totalCount: data.pages[0].count,
      }),
      enabled:
        (getCachedParticipants()?.length === 0 || shouldDisableCache) &&
        Boolean(chatRoomUuid),
      getNextPageParam: lastPage =>
        lastPage.has_next_page ? lastPage.page + 1 : undefined,
    },
  );

  const { totalCount = 0 }: any = data || {};

  const memoizedParticipants = useMemo(
    () => getCachedParticipants(),
    [data?.pages],
  );

  return {
    chatParticipants: memoizedParticipants,
    isLoading,
    refetchChatParticipants: chatRoomUuid ? refetchChatParticipants : noop,
    hasNextPage: hasNextPage,
    totalCount,
    fetchNextPage,
  };
};
